import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Download, RefreshCw, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';

const COLORS = [
  '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#06b6d4', '#84cc16', '#ec4899', '#6366f1', '#14b8a6'
];

const MetricaBase = ({ 
  titulo, 
  descricao, 
  endpoint, 
  tipoDownload,
  cor = 'purple',
  mostrarGraficoArea = true 
}) => {
  const [dados, setDados] = useState([]);
  const [totalGeral, setTotalGeral] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadDados();
  }, [endpoint]);

  const loadDados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/metricas/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDados(response.data.dados || []);
      setTotalGeral(response.data.total_geral || 0);
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/metricas/download-excel/${tipoDownload}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `metrica_${tipoDownload}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Download concluído com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com ações */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{titulo}</h2>
          <p className="text-gray-600">{descricao}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={loadDados}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Atualizar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className={`flex items-center gap-2 bg-${cor}-500 hover:bg-${cor}-600`}
          >
            <Download size={16} />
            {downloading ? 'Gerando...' : 'Download Excel'}
          </Button>
        </div>
      </div>

      {/* Card com total */}
      <Card className={`border-l-4 border-l-${cor}-500`}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className={`p-4 bg-${cor}-100 rounded-lg`}>
              <TrendingUp className={`text-${cor}-600`} size={32} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total de Colaboradores</p>
              <p className="text-3xl font-bold text-gray-800">{totalGeral}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Pizza */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição (Pizza)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dados}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ categoria, percentual }) => `${categoria}: ${percentual}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="total"
                >
                  {dados.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Barras */}
        <Card>
          <CardHeader>
            <CardTitle>Comparativo (Barras)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dados}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="categoria" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill={COLORS[0]} name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Área (se aplicável) */}
      {mostrarGraficoArea && (
        <Card>
          <CardHeader>
            <CardTitle>Tendência (Área)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dados}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="categoria" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  stroke={COLORS[0]} 
                  fill={COLORS[0]} 
                  fillOpacity={0.6}
                  name="Total"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabela de dados */}
      <Card>
        <CardHeader>
          <CardTitle>Detalhamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left">Categoria</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-right">Percentual</th>
                </tr>
              </thead>
              <tbody>
                {dados.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-2">{item.categoria}</td>
                    <td className="px-4 py-2 text-right font-semibold">{item.total}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={`inline-block px-2 py-1 rounded text-white bg-${cor}-500`}>
                        {item.percentual}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MetricaBase;
